import { useState, useEffect } from "react";
import { getWoodrowStrategy, updateWoodrowStrategy, woodrowReset } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Settings, Check, ShieldCheck, Flame, Zap, BarChart3, TrendingUp, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const RISK_OPTIONS = [
  { value: "conservative", label: "Conservative", desc: "Capital preservation first", icon: ShieldCheck, color: "text-bt-positive" },
  { value: "moderate", label: "Moderate", desc: "Balanced growth & safety", icon: BarChart3, color: "text-bt-gold" },
  { value: "aggressive", label: "Aggressive", desc: "Max returns, higher risk", icon: Flame, color: "text-bt-negative" },
];

const VOL_OPTIONS = [
  { value: "low", label: "Low", desc: "Stable blue-chips" },
  { value: "medium", label: "Medium", desc: "Mixed growth" },
  { value: "high", label: "High", desc: "High-beta plays" },
];

const SECTORS = [
  "Technology", "Healthcare", "Financials", "Consumer Cyclical",
  "Consumer Defensive", "Energy", "Communication", "Industrials", "Utilities", "Real Estate", "Materials", "Crypto",
];

export default function WoodrowStrategy() {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    getWoodrowStrategy()
      .then(r => { setStrategy(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const update = (key, val) => {
    setStrategy(s => ({ ...s, [key]: val }));
    setDirty(true);
  };

  const updateSector = (sector, val) => {
    setStrategy(s => ({
      ...s,
      sector_weights: { ...s.sector_weights, [sector]: val },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateWoodrowStrategy(strategy);
      setStrategy(res.data);
      setDirty(false);
      toast.success("Strategy settings saved — Woodrow will use these on the next cycle");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-bt-surface border border-bt-border-subtle p-3 sm:p-4" data-testid="woodrow-strategy">
        <Skeleton className="h-4 w-40 bg-bt-surface-2 mb-3" />
        <Skeleton className="h-32 bg-bt-surface-2" />
      </div>
    );
  }

  if (!strategy) return null;

  return (
    <div className="bg-bt-surface border border-bt-border-subtle p-3 sm:p-4" data-testid="woodrow-strategy">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary flex items-center gap-2">
          <Settings className="w-3.5 h-3.5 text-bt-gold" strokeWidth={1.5} />
          Strategy Settings
        </h3>
        {dirty && (
          <Button
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-mono text-[10px] h-6 px-2"
            data-testid="strategy-save-btn"
          >
            <Check className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      {/* Risk Tolerance */}
      <div className="mb-4">
        <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-2">Risk Tolerance</p>
        <div className="grid grid-cols-3 gap-1.5" data-testid="strategy-risk-options">
          {RISK_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => update("risk_tolerance", opt.value)}
              className={`px-2 py-2 border text-center transition-all ${
                strategy.risk_tolerance === opt.value
                  ? "border-bt-gold bg-bt-gold/10"
                  : "border-bt-border-subtle hover:border-bt-border-focus"
              }`}
              data-testid={`strategy-risk-${opt.value}`}
            >
              <opt.icon className={`w-4 h-4 mx-auto mb-1 ${strategy.risk_tolerance === opt.value ? opt.color : "text-bt-text-tertiary"}`} />
              <p className={`font-mono text-[10px] font-medium ${strategy.risk_tolerance === opt.value ? "text-bt-text-primary" : "text-bt-text-secondary"}`}>
                {opt.label}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Volatility Preference */}
      <div className="mb-4">
        <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-2">Volatility Preference</p>
        <div className="grid grid-cols-3 gap-1.5" data-testid="strategy-vol-options">
          {VOL_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => update("volatility_preference", opt.value)}
              className={`px-2 py-1.5 border text-center transition-all ${
                strategy.volatility_preference === opt.value
                  ? "border-bt-gold bg-bt-gold/10"
                  : "border-bt-border-subtle hover:border-bt-border-focus"
              }`}
              data-testid={`strategy-vol-${opt.value}`}
            >
              <p className={`font-mono text-[10px] font-medium ${strategy.volatility_preference === opt.value ? "text-bt-text-primary" : "text-bt-text-secondary"}`}>
                {opt.label}
              </p>
              <p className="text-[8px] text-bt-text-tertiary">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-3 mb-4">
        <SliderRow
          label="Max Position Size"
          value={strategy.max_position_pct}
          min={5} max={50} step={5}
          format={v => `${v}%`}
          onChange={v => update("max_position_pct", v)}
          testId="strategy-max-position"
        />
        <SliderRow
          label="Cash Reserve"
          value={strategy.cash_reserve_pct}
          min={5} max={50} step={5}
          format={v => `${v}%`}
          onChange={v => update("cash_reserve_pct", v)}
          testId="strategy-cash-reserve"
        />
        <SliderRow
          label="Stop-Loss"
          value={strategy.stop_loss_pct}
          min={2} max={15} step={1}
          format={v => `${v}%`}
          onChange={v => update("stop_loss_pct", v)}
          testId="strategy-stop-loss"
        />
        <SliderRow
          label="Trailing Stop"
          value={strategy.trailing_stop_pct}
          min={1} max={10} step={1}
          format={v => `${v}%`}
          onChange={v => update("trailing_stop_pct", v)}
          testId="strategy-trailing-stop"
        />
        <SliderRow
          label="Drawdown Protection"
          value={strategy.max_drawdown_pct || 8}
          min={10} max={100} step={5}
          format={v => v >= 100 ? 'OFF' : `${v}%`}
          onChange={v => update("max_drawdown_pct", v)}
          testId="strategy-drawdown-protection"
        />
        <SliderRow
          label="Trade Frequency"
          value={strategy.trade_frequency_min || 15}
          min={1} max={120} step={1}
          format={v => v < 60 ? `${v} min` : v === 60 ? `1 hr` : `${(v/60).toFixed(1)} hr`}
          onChange={v => update("trade_frequency_min", v)}
          testId="strategy-trade-frequency"
        />

        {/* Hold Time */}
        <div className="space-y-1.5" data-testid="strategy-hold-time">
          <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Hold Time</p>
          <div className="grid grid-cols-3 gap-1">
            {[
              { key: "day_trader", label: "Day Trader", desc: "Minutes–Hours" },
              { key: "short_term", label: "Short Term", desc: "1–5 Days" },
              { key: "long_term", label: "Long Term", desc: "Weeks–Months" },
            ].map(opt => {
              const active = (strategy.hold_time || "short_term") === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => update("hold_time", opt.key)}
                  className={`py-2 px-2 text-center border transition-all ${
                    active
                      ? "bg-bt-gold/15 border-bt-gold/40 text-bt-gold"
                      : "bg-bt-surface border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-secondary hover:border-bt-border-focus"
                  }`}
                  data-testid={`hold-time-${opt.key}`}
                >
                  <p className="text-[11px] font-mono font-medium">{opt.label}</p>
                  <p className="text-[9px] font-mono mt-0.5 opacity-60">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sector Allocation */}
      <div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-2 flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-bt-gold" /> Sector Weights
        </p>
        <div className="space-y-2" data-testid="strategy-sectors">
          {SECTORS.map(sector => {
            const weight = strategy.sector_weights?.[sector] ?? 50;
            const barColor = weight >= 70 ? "bg-bt-positive" : weight <= 30 ? "bg-bt-negative/60" : "bg-bt-gold/60";
            return (
              <div key={sector} className="flex items-center gap-2" data-testid={`strategy-sector-${sector.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="text-[10px] font-mono text-bt-text-secondary w-[110px] truncate shrink-0">{sector}</span>
                <div className="flex-1">
                  <Slider
                    value={[weight]}
                    min={0} max={100} step={10}
                    onValueChange={([v]) => updateSector(sector, v)}
                    className="h-4"
                  />
                </div>
                <div className="flex items-center gap-1 w-[50px] shrink-0 justify-end">
                  <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${Math.max(4, weight * 0.3)}px` }} />
                  <span className={`text-[10px] font-mono ${weight >= 70 ? "text-bt-positive" : weight <= 30 ? "text-bt-negative" : "text-bt-text-tertiary"}`}>
                    {weight}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, format, onChange, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span className="text-[10px] font-mono text-bt-text-secondary w-[100px] shrink-0">{label}</span>
      <div className="flex-1">
        <Slider
          value={[value]}
          min={min} max={max} step={step}
          onValueChange={([v]) => onChange(v)}
          className="h-4"
        />
      </div>
      <span className="text-xs font-mono text-bt-text-primary w-[36px] text-right">{format(value)}</span>
    </div>
  );
}
